/**
 * Consolidated Permissions Adapter
 * Maps consolidated permissions to specific permission checks
 * Reduces N RPC calls to 1-2 cached queries
 * 
 * Phase 1: AuthN/AuthZ Consolidation for Subcons page
 * Feature flags: ENABLE_CONSOLIDATED_PERMISSIONS, ENABLE_SUBCONS_PERMISSION_CONSOLIDATION
 */

import { useMemo } from 'react';
import { useConsolidatedPermissions } from './useConsolidatedPermissions';
import { useAdminCheck } from './useAdminCheck';
import { globalFeatureFlags } from '@/lib/featureFlags';

/**
 * Subcons-specific permissions adapter
 * Consolidates: SUBCONS_ACCESS, SUBCONS_VIEW, SUBCONS_EDIT, SUBCONS_MANAGE, PROCUREMENT_MANAGE_CONTRACTORS
 */
export const useSubconsConsolidatedPermissions = () => {
  // ✅ Hardcoded since 2025-10-10
  const useConsolidation = true;
  const { data: permissions, isLoading: permissionsLoading } = useConsolidatedPermissions();
  const adminCheck = useAdminCheck();
  const isAdmin = adminCheck.isAdmin === true; // Only true when confirmed

  return useMemo(() => {
    if (!useConsolidation) {
      // Flag OFF: Return null to signal fallback to individual hooks
      return {
        enabled: false,
        canAccess: null,
        canView: null,
        canEdit: null,
        canManage: null,
        canManageContractors: null,
        isLoading: false,
      };
    }

    const isLoading = permissionsLoading || adminCheck.isLoading;
    
    // Wait for admin check if still loading and no permissions yet
    if (adminCheck.isLoading && !permissions) {
      return {
        enabled: true,
        canAccess: false,
        canView: false,
        canEdit: false,
        canManage: false,
        canManageContractors: false,
        isLoading,  // Dynamic instead of hardcode true
      };
    }

    // Admins have all permissions - only when isAdmin is explicitly true
    if (isAdmin) {
      return {
        enabled: true,
        canAccess: true,
        canView: true,
        canEdit: true,
        canManage: true,
        canManageContractors: true,
        isLoading: false,
      };
    }

    // Map from consolidated permissions
    return {
      enabled: true,
      canAccess: permissions?.SUBCONS_ACCESS || permissions?.PROCUREMENT_MANAGE_CONTRACTORS || false,
      canView: permissions?.SUBCONS_VIEW || false,
      canEdit: permissions?.SUBCONS_EDIT || false,
      canManage: permissions?.SUBCONS_MANAGE || false,
      canManageContractors: permissions?.PROCUREMENT_MANAGE_CONTRACTORS || false,
      isLoading,
    };
  }, [useConsolidation, permissions, isAdmin, permissionsLoading, adminCheck.isLoading]);
};

/**
 * Subcons Global Reports permissions adapter
 * Consolidates: SUBCONS_GLOBAL_VIEW_DATE, SUBCONS_GLOBAL_VIEW_SUBCONS, SUBCONS_GLOBAL_VIEW_PO, SUBCONS_GLOBAL_EXPORT
 */
export const useSubconsGlobalConsolidatedPermissions = () => {
   // ✅ Hardcoded since 2025-10-10
   const useConsolidation = true;
  const { data: permissions, isLoading: permissionsLoading } = useConsolidatedPermissions();
  const adminCheck = useAdminCheck();
  const isAdmin = adminCheck.isAdmin === true; // Only true when confirmed

  return useMemo(() => {
    if (!useConsolidation) {
      // Flag OFF: Return null to signal fallback
      return {
        enabled: false,
        viewByDate: null,
        viewBySubcons: null,
        viewPO: null,
        canExport: null,
        isLoading: false,
      };
    }

    const isLoading = permissionsLoading || adminCheck.isLoading;

    // Wait for admin check if still loading and no permissions yet
    if (adminCheck.isLoading && !permissions) {
      return {
        enabled: true,
        viewByDate: false,
        viewBySubcons: false,
        viewPO: false,
        canExport: false,
        isLoading,  // Dynamic instead of hardcode true
      };
    }

    // Admins have all permissions - only when isAdmin is explicitly true
    if (isAdmin) {
      return {
        enabled: true,
        viewByDate: true,
        viewBySubcons: true,
        viewPO: true,
        canExport: true,
        isLoading: false,
      };
    }

    // Map from consolidated permissions
    return {
      enabled: true,
      viewByDate: permissions?.SUBCONS_GLOBAL_VIEW_DATE || false,
      viewBySubcons: permissions?.SUBCONS_GLOBAL_VIEW_SUBCONS || false,
      viewPO: permissions?.SUBCONS_GLOBAL_VIEW_PO || false,
      canExport: permissions?.SUBCONS_GLOBAL_EXPORT || false,
      isLoading,
    };
  }, [useConsolidation, permissions, isAdmin, permissionsLoading, adminCheck.isLoading]);
};

/**
 * Items-specific permissions adapter
 * Consolidates common Items permissions
 */
export const useItemsConsolidatedPermissions = () => {
  // ✅ Hardcoded since 2025-10-10
  const useConsolidation = true;
  const { data: permissions, isLoading: permissionsLoading } = useConsolidatedPermissions();
  const { isAdmin = false, isLoading: adminLoading } = useAdminCheck();

  return useMemo(() => {
    if (!useConsolidation) {
      return {
        enabled: false,
        canAccess: null,
        canViewInProject: null,
        canEdit: null,
        canReorder: null,
        isLoading: false,
      };
    }

    const isLoading = permissionsLoading || adminLoading;

    if (isAdmin) {
      return {
        enabled: true,
        canAccess: true,
        canViewInProject: true,
        canEdit: true,
        canReorder: true,
        isLoading,
      };
    }

    return {
      enabled: true,
      canAccess: permissions?.ITEMS_ACCESS || false,
      canViewInProject: permissions?.ITEMS_VIEW_IN_PROJECT || false,
      canEdit: permissions?.ITEMS_EDIT_BASIC || permissions?.ITEMS_EDIT_FUNCTIONS || false,
      canReorder: permissions?.ITEMS_REORDER || false,
      isLoading,
    };
  }, [useConsolidation, permissions, isAdmin, permissionsLoading, adminLoading]);
};

/**
 * Specs-specific permissions adapter
 * Consolidates common Specs permissions
 */
export const useSpecsConsolidatedPermissions = () => {
   // ✅ Hardcoded since 2025-10-10
   const useConsolidation = true;
  const { data: permissions, isLoading: permissionsLoading } = useConsolidatedPermissions();
  const { isAdmin = false, isLoading: adminLoading } = useAdminCheck();

  return useMemo(() => {
    if (!useConsolidation) {
      return {
        enabled: false,
        canAccess: null,
        canViewItems: null,
        canEditItems: null,
        canManageLayers: null,
        isLoading: false,
      };
    }

    const isLoading = permissionsLoading || adminLoading;

    if (isAdmin) {
      return {
        enabled: true,
        canAccess: true,
        canViewItems: true,
        canEditItems: true,
        canManageLayers: true,
        isLoading,
      };
    }

    return {
      enabled: true,
      canAccess: permissions?.SPECS_ACCESS || false,
      canViewItems: permissions?.SPECS_ITEMS_VIEW || false,
      canEditItems: permissions?.SPECS_ITEMS_EDIT || false,
      canManageLayers: permissions?.SPECS_MANAGE_LAYERS || false,
      isLoading,
    };
  }, [useConsolidation, permissions, isAdmin, permissionsLoading, adminLoading]);
};

/**
 * Procurement-specific permissions adapter
 * Consolidates common Procurement permissions
 */
export const useProcurementConsolidatedPermissions = () => {
  // ✅ Hardcoded since 2025-10-10
  const useConsolidation = true;
  const { data: permissions, isLoading: permissionsLoading } = useConsolidatedPermissions();
  const { isAdmin = false, isLoading: adminLoading } = useAdminCheck();

  return useMemo(() => {
    if (!useConsolidation) {
      return {
        enabled: false,
        canCreateOrder: null,
        canEditOrder: null,
        canDeleteOrder: null,
        canManageContractors: null,
        isLoading: false,
      };
    }

    const isLoading = permissionsLoading || adminLoading;

    if (isAdmin) {
      return {
        enabled: true,
        canCreateOrder: true,
        canEditOrder: true,
        canDeleteOrder: true,
        canManageContractors: true,
        isLoading,
      };
    }

    return {
      enabled: true,
    canCreateOrder: permissions?.PROCUREMENT_CREATE_ORDER || false,
    canEditOrder: permissions?.PROCUREMENT_EDIT_ORDER || false,
    canDeleteOrder: permissions?.PROCUREMENT_DELETE_ORDER || false,
      canManageContractors: permissions?.PROCUREMENT_MANAGE_CONTRACTORS || false,
      isLoading,
    };
  }, [useConsolidation, permissions, isAdmin, permissionsLoading, adminLoading]);
};
